import {
  addDoc,
  collection,
  deleteDoc,
  getDocs,
  query,
  serverTimestamp,
  Timestamp,
  where,
} from 'firebase/firestore';
import { auth, db } from '../firebase/config';
import AIService from './AIService';
import type { App } from '../types/app';
import type {
  ParsedReviewRow,
  ReviewActionItem,
  ReviewAnalysisReport,
  ReviewAnalysisResult,
  ReviewThemeInsight,
} from '../types/reviewAnalysis';

const COLLECTION = 'appReviewAnalysisReports';
const FETCH_GOOGLE_PLAY_REVIEWS_ENDPOINT =
  process.env.REACT_APP_FETCH_GOOGLE_PLAY_REVIEWS_URL ||
  'https://europe-west1-translatorx-5774e.cloudfunctions.net/fetchGooglePlayReviews';
export type WorkItemExportPlatform = 'azure-devops' | 'jira' | 'clickup';

type AIRawActionItem = {
  title?: string;
  rationale?: string;
  implementationSteps?: string[];
  priority?: string;
  ownerSuggestion?: string;
  expectedImpact?: string;
};

type AIRawTheme = {
  theme?: string;
  summary?: string;
  evidence?: string[];
};

type AIRawResult = {
  summary?: string;
  whatIsWorkingWell?: AIRawTheme[];
  whatNeedsImprovement?: AIRawTheme[];
  badReviewActionItems?: AIRawActionItem[];
  goodReviewEnhancementItems?: AIRawActionItem[];
};

class ReviewAnalysisService {
  private async postAuthenticatedJson<T>(
    url: string,
    body: Record<string, unknown>
  ): Promise<T> {
    await auth.authStateReady();
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('Authentication required. Please sign in.');
    }

    const idToken = await currentUser.getIdToken();
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify(body),
    });

    const rawText = await response.text();
    let responseData: any = null;
    try {
      responseData = rawText ? JSON.parse(rawText) : null;
    } catch {
      responseData = null;
    }

    if (!response.ok) {
      const detailedMessage =
        responseData?.error ||
        responseData?.message ||
        (rawText && rawText.trim().length > 0 ? rawText.trim() : null);
      throw new Error(
        detailedMessage || `Request failed with status ${response.status}`
      );
    }

    return responseData as T;
  }

  private generateId(prefix: string): string {
    return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  }

  private toDate(value: any): Date {
    if (value instanceof Timestamp) return value.toDate();
    if (value instanceof Date) return value;
    return new Date(value);
  }

  parseReviewsCSV(file: File): Promise<ParsedReviewRow[]> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        try {
          const text = String(reader.result || '');
          const rows = this.parseCSV(text);
          if (rows.length === 0) {
            resolve([]);
            return;
          }
          const headers = rows[0].map((header) => header.trim().toLowerCase());
          const dataRows = rows.slice(1);

          const reviews = dataRows
            .map((row) => this.mapRowToReview(headers, row))
            .filter((item) => item.body.trim().length > 0);

          resolve(reviews);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = () => reject(new Error('Failed to read CSV file.'));
      reader.readAsText(file);
    });
  }

  parseManualReviewText(input: string): ParsedReviewRow[] {
    const raw = String(input || '').trim();
    if (!raw) return [];

    const normalized = raw.replace(/\r\n/g, '\n');
    const blocks = normalized
      .split(/\n\s*\n+/)
      .map((block) => block.trim())
      .filter(Boolean);

    const lines = normalized
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);

    const candidates = blocks.length > 1 ? blocks : lines;
    return candidates
      .map((candidate) => this.parseManualReviewRow(candidate))
      .filter((row): row is ParsedReviewRow => Boolean(row));
  }

  async fetchGooglePlayReviews(input: {
    appId: string;
    packageName: string;
    maxResults?: number;
    startIndex?: number;
    token?: string;
    translationLanguage?: string;
  }): Promise<{
    reviews: ParsedReviewRow[];
    nextPageToken?: string;
    fetchedCount: number;
    apiReviewCount: number;
    pageInfo?: { totalResults?: number; resultPerPage?: number; startIndex?: number } | null;
    message: string;
  }> {
    const response = await this.postAuthenticatedJson<{
      reviews?: Array<{
        authorName?: string;
        text?: string;
        originalText?: string;
        starRating?: number;
        appVersionCode?: string;
        lastModified?: string;
      }>;
      fetchedCount?: number;
      apiReviewCount?: number;
      message?: string;
      tokenPagination?: {
        nextPageToken?: string;
        previousPageToken?: string;
      };
      pageInfo?: {
        totalResults?: number;
        resultPerPage?: number;
        startIndex?: number;
      };
    }>(FETCH_GOOGLE_PLAY_REVIEWS_ENDPOINT, {
      appId: String(input.appId || '').trim(),
      packageName: String(input.packageName || '').trim(),
      maxResults: input.maxResults,
      startIndex: input.startIndex,
      token: String(input.token || '').trim() || undefined,
      translationLanguage:
        String(input.translationLanguage || '').trim() || undefined,
    });

    const reviews = Array.isArray(response?.reviews)
      ? response.reviews
          .map((item) => ({
            rating:
              typeof item?.starRating === 'number'
                ? Number(item.starRating)
                : undefined,
            body: String(item?.text || item?.originalText || '').trim(),
            reviewer: item?.authorName ? String(item.authorName).trim() : undefined,
            date: item?.lastModified ? String(item.lastModified).trim() : undefined,
            appVersion: item?.appVersionCode
              ? String(item.appVersionCode).trim()
              : undefined,
            source: 'google-play' as const,
          }))
          .map((item) => {
            if (item.body.length > 0) return item;
            if (typeof item.rating === 'number') {
              return {
                ...item,
                body: `Rating-only review (${item.rating} stars).`,
              };
            }
            return item;
          })
          .filter((item) => item.body.length > 0)
      : [];

    return {
      reviews,
      nextPageToken: response?.tokenPagination?.nextPageToken || undefined,
      fetchedCount: Number(response?.fetchedCount || reviews.length),
      apiReviewCount: Number(response?.apiReviewCount || 0),
      pageInfo: response?.pageInfo || null,
      message: String(response?.message || 'Google Play reviews fetched.'),
    };
  }

  private parseManualReviewRow(input: string): ParsedReviewRow | null {
    const text = String(input || '').trim();
    if (!text) return null;

    const ratingMatch =
      text.match(/^([1-5](?:\.\d)?)\s*(?:\/\s*5)?\s*[-:]\s*(.+)$/) ||
      text.match(/^([1-5](?:\.\d)?)\s*(?:star|stars)\s*[-:]\s*(.+)$/i);

    if (ratingMatch) {
      const rating = Number(ratingMatch[1]);
      const body = String(ratingMatch[2] || '').trim();
      if (!body) return null;
      return {
        rating: Number.isFinite(rating) ? rating : undefined,
        body,
        source: 'unknown',
      };
    }

    return {
      body: text,
      source: 'unknown',
    };
  }

  private parseCSV(input: string): string[][] {
    const rows: string[][] = [];
    let currentRow: string[] = [];
    let currentValue = '';
    let inQuotes = false;

    for (let i = 0; i < input.length; i++) {
      const char = input[i];
      const next = input[i + 1];

      if (char === '"') {
        if (inQuotes && next === '"') {
          currentValue += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
        continue;
      }

      if (char === ',' && !inQuotes) {
        currentRow.push(currentValue);
        currentValue = '';
        continue;
      }

      if ((char === '\n' || char === '\r') && !inQuotes) {
        if (char === '\r' && next === '\n') i++;
        currentRow.push(currentValue);
        rows.push(currentRow);
        currentRow = [];
        currentValue = '';
        continue;
      }

      currentValue += char;
    }

    if (currentValue.length > 0 || currentRow.length > 0) {
      currentRow.push(currentValue);
      rows.push(currentRow);
    }

    return rows.filter((row) => row.some((cell) => cell.trim().length > 0));
  }

  private getByHeader(headers: string[], row: string[], aliases: string[]): string {
    const index = headers.findIndex((header) =>
      aliases.some((alias) => header.includes(alias))
    );
    return index >= 0 ? String(row[index] || '').trim() : '';
  }

  private mapRowToReview(headers: string[], row: string[]): ParsedReviewRow {
    const ratingRaw = this.getByHeader(headers, row, ['rating', 'stars', 'score']);
    const title = this.getByHeader(headers, row, ['title', 'headline', 'subject']);
    const body = this.getByHeader(headers, row, ['review', 'content', 'comment', 'text', 'body']);
    const date = this.getByHeader(headers, row, ['date', 'time', 'at', 'created']);
    const reviewer = this.getByHeader(headers, row, ['user', 'author', 'reviewer', 'name']);
    const appVersion = this.getByHeader(headers, row, ['version', 'app version']);
    const sourceRaw = this.getByHeader(headers, row, ['source', 'store', 'platform']).toLowerCase();
    const source =
      sourceRaw.includes('google') || sourceRaw.includes('play')
        ? 'google-play'
        : sourceRaw.includes('apple') || sourceRaw.includes('ios')
        ? 'apple-app-store'
        : 'unknown';

    const rating = Number(ratingRaw);
    return {
      rating: Number.isFinite(rating) ? rating : undefined,
      title,
      body,
      date: date || undefined,
      reviewer: reviewer || undefined,
      appVersion: appVersion || undefined,
      source,
    };
  }

  private parseJSON<T>(text: string): T | null {
    const value = String(text || '').trim();
    if (!value) return null;

    try {
      return JSON.parse(value) as T;
    } catch {
      // continue
    }

    const fenced =
      value.match(/```json\s*([\s\S]*?)\s*```/i)?.[1] ||
      value.match(/```\s*([\s\S]*?)\s*```/i)?.[1];
    if (fenced) {
      try {
        return JSON.parse(fenced) as T;
      } catch {
        // continue
      }
    }

    const firstBrace = value.indexOf('{');
    const lastBrace = value.lastIndexOf('}');
    if (firstBrace >= 0 && lastBrace > firstBrace) {
      try {
        return JSON.parse(value.slice(firstBrace, lastBrace + 1)) as T;
      } catch {
        return null;
      }
    }

    return null;
  }

  private normalizeThemes(input: AIRawTheme[] | undefined): ReviewThemeInsight[] {
    if (!Array.isArray(input)) return [];
    return input
      .map((item) => ({
        theme: String(item?.theme || '').trim(),
        summary: String(item?.summary || '').trim(),
        evidence: Array.isArray(item?.evidence)
          ? item.evidence.map((e) => String(e || '').trim()).filter(Boolean)
          : [],
      }))
      .filter((item) => item.theme.length > 0 || item.summary.length > 0);
  }

  private normalizeActionItems(input: AIRawActionItem[] | undefined): ReviewActionItem[] {
    if (!Array.isArray(input)) return [];
    return input
      .map((item) => {
        const rawPriority = String(item?.priority || 'medium').toLowerCase();
        const priority =
          rawPriority === 'high' || rawPriority === 'low' ? rawPriority : 'medium';
        return {
          id: this.generateId('review_action'),
          title: String(item?.title || '').trim(),
          rationale: String(item?.rationale || '').trim(),
          implementationSteps: Array.isArray(item?.implementationSteps)
            ? item.implementationSteps.map((step) => String(step || '').trim()).filter(Boolean)
            : [],
          priority,
          ownerSuggestion: String(item?.ownerSuggestion || '').trim() || undefined,
          expectedImpact: String(item?.expectedImpact || '').trim() || undefined,
        } as ReviewActionItem;
      })
      .filter((item) => item.title.length > 0);
  }

  private stripUndefined(value: any): any {
    if (Array.isArray(value)) return value.map((item) => this.stripUndefined(item));
    if (value && typeof value === 'object') {
      return Object.fromEntries(
        Object.entries(value)
          .filter(([, entry]) => entry !== undefined)
          .map(([key, entry]) => [key, this.stripUndefined(entry)])
      );
    }
    return value;
  }

  private csvEscape(value: string): string {
    const text = String(value ?? '');
    if (text.includes('"') || text.includes(',') || text.includes('\n')) {
      return `"${text.replace(/"/g, '""')}"`;
    }
    return text;
  }

  private buildWorkItemDescription(
    report: ReviewAnalysisReport,
    item: ReviewActionItem,
    category: string
  ): string {
    const steps = item.implementationSteps
      .map((step, index) => `${index + 1}. ${step}`)
      .join('\n');
    const owner = item.ownerSuggestion ? `\nOwner Suggestion: ${item.ownerSuggestion}` : '';
    const impact = item.expectedImpact ? `\nExpected Impact: ${item.expectedImpact}` : '';
    return [
      `App: ${report.appName}`,
      `Report ID: ${report.reportId}`,
      `Category: ${category}`,
      `Priority: ${item.priority}`,
      '',
      `Rationale: ${item.rationale}`,
      '',
      'Implementation Steps:',
      steps || '-',
      owner,
      impact,
    ]
      .join('\n')
      .trim();
  }

  private downloadCSV(filename: string, headers: string[], rows: string[][]): void {
    const content = [
      headers.map((item) => this.csvEscape(item)).join(','),
      ...rows.map((row) => row.map((item) => this.csvEscape(item)).join(',')),
    ].join('\n');
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  private getPlatformCSVConfig(platform: WorkItemExportPlatform): {
    headers: string[];
    row: (input: {
      report: ReviewAnalysisReport;
      item: ReviewActionItem;
      category: string;
    }) => string[];
    filenamePrefix: string;
  } {
    switch (platform) {
      case 'azure-devops':
        return {
          headers: ['Work Item Type', 'Title', 'Description', 'Tags'],
          row: ({ report, item, category }) => [
            'Task',
            item.title,
            this.buildWorkItemDescription(report, item, category),
            `${report.appName};${category};review-analysis`,
          ],
          filenamePrefix: 'azure-devops',
        };
      case 'jira':
        return {
          headers: ['Summary', 'Issue Type', 'Description', 'Priority', 'Labels'],
          row: ({ report, item, category }) => [
            item.title,
            'Task',
            this.buildWorkItemDescription(report, item, category),
            item.priority,
            `${report.appName.replace(/\s+/g, '-').toLowerCase()} ${category
              .replace(/\s+/g, '-')
              .toLowerCase()} review-analysis`,
          ],
          filenamePrefix: 'jira',
        };
      case 'clickup':
      default:
        return {
          headers: ['Task Name', 'Description', 'Priority', 'Tags'],
          row: ({ report, item, category }) => [
            item.title,
            this.buildWorkItemDescription(report, item, category),
            item.priority,
            `${report.appName},${category},review-analysis`,
          ],
          filenamePrefix: 'clickup',
        };
    }
  }

  exportWorkItem(input: {
    report: ReviewAnalysisReport;
    item: ReviewActionItem;
    category: 'bad-review-actions' | 'good-review-enhancements';
    platform: WorkItemExportPlatform;
  }): void {
    const config = this.getPlatformCSVConfig(input.platform);
    const categoryLabel =
      input.category === 'bad-review-actions'
        ? 'Bad Review Action'
        : 'Good Review Enhancement';
    const row = config.row({
      report: input.report,
      item: input.item,
      category: categoryLabel,
    });
    const slug = input.item.title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 50);
    const filename = `${config.filenamePrefix}-${input.report.appName
      .replace(/[^a-z0-9]+/gi, '-')
      .toLowerCase()}-${slug || 'work-item'}.csv`;
    this.downloadCSV(filename, config.headers, [row]);
  }

  exportAllWorkItems(input: {
    report: ReviewAnalysisReport;
    platform: WorkItemExportPlatform;
  }): void {
    const config = this.getPlatformCSVConfig(input.platform);
    const allItems: Array<{ item: ReviewActionItem; category: string }> = [
      ...input.report.analysis.badReviewActionItems.map((item) => ({
        item,
        category: 'Bad Review Action',
      })),
      ...input.report.analysis.goodReviewEnhancementItems.map((item) => ({
        item,
        category: 'Good Review Enhancement',
      })),
    ];
    const rows = allItems.map(({ item, category }) =>
      config.row({
        report: input.report,
        item,
        category,
      })
    );
    const filename = `${config.filenamePrefix}-${input.report.appName
      .replace(/[^a-z0-9]+/gi, '-')
      .toLowerCase()}-all-work-items.csv`;
    this.downloadCSV(filename, config.headers, rows);
  }

  private buildPrompt(reviews: ParsedReviewRow[], avgRating?: number): string {
    const sampled = reviews.slice(0, 200).map((review, index) => {
      const ratingPart = review.rating ? `rating=${review.rating}` : 'rating=N/A';
      const titlePart = review.title ? `title="${review.title}"` : 'title=N/A';
      return `${index + 1}. ${ratingPart}; ${titlePart}; body="${review.body}"`;
    });

    return [
      'You are an expert app growth and product quality analyst.',
      'Analyze the uploaded app store reviews and produce practical, execution-ready insights.',
      '',
      `Review count: ${reviews.length}`,
      `Average rating: ${avgRating !== undefined ? avgRating.toFixed(2) : 'N/A'}`,
      '',
      'Output requirements:',
      '- whatIsWorkingWell: themes from positive reviews and what to reinforce.',
      '- whatNeedsImprovement: themes from negative reviews and root causes.',
      '- badReviewActionItems: concrete tasks to reduce poor reviews.',
      '- goodReviewEnhancementItems: concrete tasks to amplify strengths and make them even better.',
      '- Every action item must include rationale and implementationSteps.',
      '- Keep actions specific, measurable, and actionable.',
      '- Return valid JSON only with this shape:',
      '{ "summary": string, "whatIsWorkingWell": [{ "theme": string, "summary": string, "evidence": string[] }], "whatNeedsImprovement": [{ "theme": string, "summary": string, "evidence": string[] }], "badReviewActionItems": [{ "title": string, "rationale": string, "implementationSteps": string[], "priority": "high|medium|low", "ownerSuggestion": string, "expectedImpact": string }], "goodReviewEnhancementItems": [{ "title": string, "rationale": string, "implementationSteps": string[], "priority": "high|medium|low", "ownerSuggestion": string, "expectedImpact": string }] }',
      '',
      'Reviews:',
      ...sampled,
    ].join('\n');
  }

  async analyzeReviews(input: {
    userId: string;
    app: App;
    reviews: ParsedReviewRow[];
    model?: string;
  }): Promise<ReviewAnalysisReport> {
    const start = Date.now();
    if (!input.userId) throw new Error('User is required.');
    if (!input.app?.appId) throw new Error('Selected app is required.');
    if (!Array.isArray(input.reviews) || input.reviews.length === 0) {
      throw new Error('At least one parsed review is required.');
    }

    const rated = input.reviews.filter((item) => typeof item.rating === 'number');
    const avgRating =
      rated.length > 0
        ? rated.reduce((sum, item) => sum + Number(item.rating || 0), 0) / rated.length
        : undefined;

    const response = await AIService.generateText(
      this.buildPrompt(input.reviews, avgRating),
      {
        model: input.model || undefined,
        temperature: 0.2,
        maxTokens: 4096,
      }
    );

    const parsed = this.parseJSON<AIRawResult>(response.text) || {};
    const analysis: ReviewAnalysisResult = {
      summary: String(parsed.summary || 'Review analysis completed.').trim(),
      whatIsWorkingWell: this.normalizeThemes(parsed.whatIsWorkingWell),
      whatNeedsImprovement: this.normalizeThemes(parsed.whatNeedsImprovement),
      badReviewActionItems: this.normalizeActionItems(parsed.badReviewActionItems),
      goodReviewEnhancementItems: this.normalizeActionItems(parsed.goodReviewEnhancementItems),
    };

    return {
      reportId: this.generateId('review_analysis'),
      userId: input.userId,
      appId: input.app.appId,
      appName: input.app.name,
      modelUsed: input.model || AIService.getDefaultModel(),
      reviewCount: input.reviews.length,
      avgRating,
      analysis,
      metadata: {
        generatedAt: new Date(),
        durationSeconds: Number(((Date.now() - start) / 1000).toFixed(2)),
      },
    };
  }

  async saveReport(report: ReviewAnalysisReport): Promise<void> {
    const payload = this.stripUndefined({
      ...report,
      metadata: {
        ...report.metadata,
        generatedAt: report.metadata.generatedAt || new Date(),
      },
      savedAt: serverTimestamp(),
    });
    await addDoc(collection(db, COLLECTION), payload);
  }

  async getReportsByApp(userId: string, appId: string): Promise<ReviewAnalysisReport[]> {
    const q = query(
      collection(db, COLLECTION),
      where('userId', '==', userId),
      where('appId', '==', appId)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs
      .map((item) => {
        const data = item.data() as any;
        return {
          reportId: data.reportId,
          userId: data.userId,
          appId: data.appId,
          appName: data.appName,
          modelUsed: data.modelUsed || AIService.getDefaultModel(),
          reviewCount: Number(data.reviewCount || 0),
          avgRating:
            typeof data.avgRating === 'number' ? Number(data.avgRating) : undefined,
          analysis: {
            summary: String(data?.analysis?.summary || '').trim(),
            whatIsWorkingWell: this.normalizeThemes(data?.analysis?.whatIsWorkingWell),
            whatNeedsImprovement: this.normalizeThemes(data?.analysis?.whatNeedsImprovement),
            badReviewActionItems: this.normalizeActionItems(
              data?.analysis?.badReviewActionItems
            ),
            goodReviewEnhancementItems: this.normalizeActionItems(
              data?.analysis?.goodReviewEnhancementItems
            ),
          },
          metadata: {
            generatedAt: this.toDate(data?.metadata?.generatedAt || new Date()),
            durationSeconds: Number(data?.metadata?.durationSeconds || 0),
          },
          savedAt: data?.savedAt ? this.toDate(data.savedAt) : undefined,
        } as ReviewAnalysisReport;
      })
      .sort(
        (a, b) =>
          (b.savedAt || b.metadata.generatedAt).getTime() -
          (a.savedAt || a.metadata.generatedAt).getTime()
      );
  }

  async deleteReport(userId: string, reportId: string): Promise<void> {
    const q = query(
      collection(db, COLLECTION),
      where('userId', '==', userId),
      where('reportId', '==', reportId)
    );
    const snapshot = await getDocs(q);
    await Promise.all(snapshot.docs.map((item) => deleteDoc(item.ref)));
  }
}

export default new ReviewAnalysisService();
